<script>
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

/**
 * Cabeçalho fixo do painel de detalhes.
 *
 * Fica FORA do scroll: é o único lugar que identifica de quem é a conversa
 * aberta, e os dois botões (destacar o painel e fechá-lo) precisam continuar
 * alcançáveis com a lista de seções rolada até o fim.
 *
 * Os dois botões se alternam no ponto de quebra `md`, e por motivos opostos:
 *
 * - **Destacar** (`hidden md:inline-flex`): abaixo de 768px não existe painel
 *   destacável. A regra veio junto do `ContactInfo.vue`, de onde este cabeçalho
 *   herdou o comportamento; o estilo, porém, usa os tokens do projeto, e não os
 *   hex literais que estavam lá.
 * - **Fechar** (`flex md:hidden`): de 768px para cima quem fecha o painel é o
 *   botão de detalhes do cabeçalho da conversa, e dois controles para a mesma
 *   ação, lado a lado, só ocupam espaço. Abaixo disso ele é indispensável: o
 *   painel vira `absolute` e cobre a tela inteira (ver o `@media` do
 *   `ConversationBox.vue`), então o cabeçalho da conversa — e o botão de lá —
 *   ficam fora de alcance, e sem este chevron não haveria saída.
 *
 * Ao lado do nome não fica mais nada. Passaram por aqui um link para o perfil
 * e um relógio com a data de cadastro, os dois removidos a pedido e pelo mesmo
 * motivo: ícone de 12px pendurado no nome cobra mira, e o que ele escondia
 * numa dica de ferramenta só aparecia para quem passasse o mouse por cima. O
 * perfil se alcança pelo nome no cabeçalho da conversa; a data de cadastro é
 * hoje a última linha de "Dados do contato".
 */
export default {
  components: {
    DashboardIcon,
    Thumbnail,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    isContactPanelDetached: {
      type: Boolean,
      default: false,
    },
    // Os quatro props abaixo existem para a lista de Contatos (#1652), que usa
    // este mesmo cabecalho sem ser uma conversa. Os padroes reproduzem o
    // comportamento do painel da conversa: destacar visivel em `md`+, fechar so
    // abaixo de `md`, com o chevron.
    showDetachButton: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    // Fora da conversa nao ha outro botao que feche o painel: o fechar fica
    // visivel em qualquer largura.
    closeAlwaysVisible: {
      type: Boolean,
      default: false,
    },
    closeIcon: {
      type: String,
      default: 'chevron-right',
    },
  },
  computed: {
    displayName() {
      return this.contact.name || this.$t('EDIT_CONTACT.UNNAMED');
    },
    contactPanelDetachToggleText() {
      return this.isContactPanelDetached
        ? this.$t('CONVERSATION.HEADER.ATTACH_DETAILS')
        : this.$t('CONVERSATION.HEADER.DETACH_DETAILS');
    },
    contactPanelDetachToggleIcon() {
      return this.isContactPanelDetached
        ? 'lock-open-outline'
        : 'lock-closed-outline';
    },
  },
  methods: {
    onPanelToggle() {
      this.$emit('togglePanel');
    },
    onContactPanelDetachToggle() {
      this.$emit('contactPanelDetachToggle');
    },
  },
};
</script>

<template>
  <div class="flex items-center w-full gap-3">
    <Thumbnail
      :src="contact.thumbnail"
      size="46px"
      :username="displayName"
      :status="contact.availability_status"
    />
    <span class="text-base font-semibold truncate grow text-body">
      {{ displayName }}
    </span>
    <div class="flex items-center gap-1 shrink-0">
      <button
        v-if="showDetachButton"
        v-tooltip.left="contactPanelDetachToggleText"
        type="button"
        class="items-center justify-center hidden w-9 h-9 p-0 transition-colors duration-150 border rounded-lg cursor-pointer md:inline-flex text-body bg-surface-sunken dark:bg-surface-raised border-line hover:bg-line-faint dark:hover:bg-surface-hover"
        :aria-label="contactPanelDetachToggleText"
        :aria-pressed="isContactPanelDetached.toString()"
        @click="onContactPanelDetachToggle"
      >
        <DashboardIcon :icon="contactPanelDetachToggleIcon" size="20" />
      </button>
      <button
        v-if="showCloseButton"
        type="button"
        class="flex items-center justify-center w-9 h-9 p-0 transition-colors duration-150 border rounded-lg cursor-pointer text-body bg-surface-sunken dark:bg-surface-raised border-line hover:bg-line-faint dark:hover:bg-surface-hover"
        :class="{ 'md:hidden': !closeAlwaysVisible }"
        :aria-label="$t('CONVERSATION_SIDEBAR.HEADER.CLOSE_PANEL')"
        @click="onPanelToggle"
      >
        <fluent-icon :icon="closeIcon" size="18" class="rtl:rotate-180" />
      </button>
    </div>
  </div>
</template>
